import { type MouseEventHandler } from 'react';
import type React from 'react';

import Grid from '@mui/material/Grid';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import Checkbox from 'components/checkbox/Checkbox';
import { TMXDialogActions, TMXDialogContent } from 'components/dialogs/TMXDialog';
import { BodyLargeBold, BodyRegular } from 'components/typography/Typography';
import { type FlightProfile } from 'typedefs/store/tmxTypes';

import { useStyles } from './styles';
import { getFlightDisabledInfo } from './utils';

interface SelectDrawsFormValues {
  selectedDraws: string[];
}

interface SelectDrawsFormProps {
  title?: React.ReactNode;
  flights?: FlightProfile[];
  selectedIds?: string[];
  loading?: boolean;
  isRemoving?: boolean;
  onSubmit?: (formValues: SelectDrawsFormValues) => void;
  onClose?: MouseEventHandler;
}

const DrawLabel: React.FC<React.PropsWithChildren<{ label?: string; info?: string; disabled?: boolean }>> = ({
  label,
  info,
  disabled,
}) => {
  const { classes } = useStyles();

  return (
    <>
      <BodyLargeBold className={disabled ? classes.drawCheckboxLabelDisabled : undefined}>{label}</BodyLargeBold>
      <BodyRegular className={disabled ? classes.drawCheckboxLabelDisabled : undefined}>{info}</BodyRegular>
    </>
  );
};

const SelectDrawsForm: React.FC<React.PropsWithChildren<SelectDrawsFormProps>> = ({
  title,
  flights,
  selectedIds,
  loading,
  isRemoving,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const initialValues = { selectedDraws: [] };

  return (
    <Formik enableReinitialize validateOnMount initialValues={initialValues} onSubmit={onSubmit}>
      {({ isSubmitting, values }) => (
        <Form>
          {title}
          <TMXDialogContent className={classes.selectDrawFormContent}>
            <Grid container direction="column" spacing={3}>
              {(flights || []).map(({ drawId, drawName, drawEntries, drawDefinition }) => {
                const { isDisabled, drawLabelInfo } = getFlightDisabledInfo({
                  isRemoving,
                  t,
                  drawDefinition,
                  drawEntries,
                  selectedIds,
                });

                return (
                  <Grid key={drawId} item>
                    <Field
                      checkboxProps={{
                        classes: { root: classes.drawCheckboxRoot },
                      }}
                      className={classes.drawCheckbox}
                      component={Checkbox}
                      disabled={isDisabled}
                      id={`selectedDraws-${drawId}`}
                      label={<DrawLabel disabled={isDisabled} info={drawLabelInfo} label={drawName} />}
                      name="selectedDraws"
                      type="checkbox"
                      value={drawId}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </TMXDialogContent>
          <TMXDialogActions>
            <StandardButton onClick={onClose}>{t('ccl')}</StandardButton>
            <StandardButton
              disabled={isSubmitting || !values.selectedDraws.length}
              loading={loading}
              type="submit"
              variant="primary"
            >
              {t('confirm')}
            </StandardButton>
          </TMXDialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default SelectDrawsForm;
